import type { HubspotFormId, HubspotRegion } from '@tmh-website/services/form.service';
import type { ReCaptchaErrorCode } from '@tmh-website/services/recaptcha.service';

import type { HubSpotOperator } from './hubspot-form-renderer.operators.service';
import type { SubmitFormProps } from './hubspot-form-renderer.service';

export interface HubSpotFormEntity {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  archived: boolean;
  fieldGroups: HubSpotFieldGroup[];
  configuration: HubSpotConfiguration;
  displayOptions: HubSpotDisplayOptions;
  legalConsentOptions: HubSpotLegalConsentOptions;
  formType: string;
}

export interface HubSpotFieldGroup {
  groupType: string;
  richTextType: string;
  fields?: HubSpotField[];
}

export const enum FieldType {
  SingleLineText = 'single_line_text',
  MultiLineText = 'multi_line_text',
  Email = 'email',
  Datepicker = 'datepicker',
  Radio = 'radio',
  SingleCheckbox = 'single_checkbox',
  MultipleCheckboxes = 'multiple_checkboxes',
  Number = 'number',
  Dropdown = 'dropdown',
  Phone = 'phone',
  File = 'file',
}

export const enum PostSubmitActionType {
  Redirect = 'redirect_url',
  Message = 'thank_you',
}

export interface HubSpotField {
  objectTypeId: string;
  name: string;
  label: string;
  required: boolean;
  hidden: boolean;
  fieldType: FieldType;
  options?: HubSpotOption[];
  validation?: HubSpotValidation;
  defaultValue?: string;
  defaultValues?: string[];
  placeholder?: string;
  description?: string;
  useCountryCodeSelect?: boolean;
  dependentFields?: HubSpotDependentField[];
}

export interface HubSpotDependentField {
  dependentCondition: HubSpotDependentCondition;
  dependentField: HubSpotField;
}

export interface HubSpotDependentCondition {
  operator: HubSpotOperator;
  values?: string[];
  value: string;
}

export interface HubSpotOption {
  label: string;
  value: string;
  description: string;
  displayOrder: number;
}

interface HubSpotValidation {
  blockedEmailDomains?: string[];
  useDefaultBlockList?: boolean;
  minAllowedDigits?: number;
  maxAllowedDigits?: number;
}

interface HubSpotConfiguration {
  language: string;
  cloneable: boolean;
  postSubmitAction: HubSpotPostSubmitAction;
  editable: boolean;
  archivable: boolean;
  recaptchaEnabled: boolean;
  notifyContactOwner: boolean;
  notifyRecipients: string[];
  createNewContactForNewEmail: boolean;
  prePopulateKnownValues: boolean;
  allowLinkToResetKnownValues: boolean;
}

interface HubSpotPostSubmitAction {
  type: PostSubmitActionType;
  value: string;
}

interface HubSpotDisplayOptions {
  renderRawHtml: boolean;
  theme: string;
  submitButtonText: string;
  style: HubSpotStyle;
  cssClass: string;
}

interface HubSpotStyle {
  fontFamily: string;
  backgroundWidth: string;
  labelTextColor: string;
  labelTextSize: string;
  helpTextColor: string;
  helpTextSize: string;
  legalConsentTextColor: string;
  legalConsentTextSize: string;
  submitColor: string;
  submitAlignment: string;
  submitFontColor: string;
  submitSize: string;
}

interface HubSpotLegalConsentOptionsCheckbox {
  required: boolean;
  subscriptionTypeId: number;
  /**
   * An HTML string
   */
  label: string;
}

/**
 * There is no documentation for this type, so this is a best guess.
 */
export enum HubSpotLegalConsentOptionsType {
  /**
   * No consent option provided.
   */
  None = 'None',

  /**
   * Consent is not needed for this application (e.g. a contact form)
   * -> We always have to set consentToProcess to true
   */
  LegitimateInterest = 'legitimate_interest',
  /**
   * A required checkbox is displayed for consentToProcess
   * -> The checkbox value is used for consentToProcess
   */
  ExplicitConsentToProcess = 'explicit_consent_to_process',
  /**
   * A text informing about consent is displayed and an action (e.g. submit) is used for consent
   * -> We alaways set the value to true
   */
  ImplicitConsentToProcess = 'implicit_consent_to_process',
}

export interface HubSpotLegalConsentOptions {
  type: HubSpotLegalConsentOptionsType;
  /**
   * HTML string. communication is usually used for marketing checkboxes.
   */
  communicationConsentText?: string;
  /**
   * HTML string. Usually optional marketing checkboxes.
   */
  communicationsCheckboxes?: HubSpotLegalConsentOptionsCheckbox[];
  /**
   * HTML string. consentToProcessText is usually used for a single legal checkboxes.
   */
  consentToProcessText?: string;
  /**
   * HTML string. A label used for the required legal checkbox.
   */
  consentToProcessCheckboxLabel?: string;
  /**
   * HTML string. A footer text for the legal checkbox
   */
  consentToProcessFooterText?: string;
  /**
   * HTML string. Usually gives information about how to revoke consent and links to the privacy policy.
   */
  privacyText?: string;
}

/**
 * What we send to HubSpot during form submission. This type is not very specific.
 */
export interface HubSpotSubmitObject {
  fields: { objectTypeId: string; name: string; value: any }[];
  hubspotFormId: HubspotFormId;
  context: HubSpotSubmitContext;
  legalConsentOptions: any;
  gRecaptchaResponse: string;
}

/**
 * A set of data containing contextual information for the submission.
 * @see https://legacydocs.hubspot.com/docs/methods/forms/submit_form_v3_authentication
 */
export interface HubSpotSubmitContext {
  /**
   * The tracking cookie token value used for HubSpot lead activity tracking.
   */
  hutk?: string;
  /**
   * The URI of the page the submission happened on.
   */
  pageUri: string;
  /**
   * The name or title of the page the submission happened on.
   */
  pageName: string;
}

/**
 * The error object that we get back from out own Proxy API
 */
export type HubSpotProxyError = HubSpotProxyErrorRecaptcha | HubSpotProxyErrorHubspot;

export interface HubSpotProxyErrorRecaptcha {
  data: null;
  errorType: ReCaptchaErrorCode;
  error: ReCaptchaErrorCode[];
}

export interface HubSpotProxyErrorHubspot {
  data: null;
  errorType: 'hubspot';
  error: string;
}

export type HubSpotFormRendererValues = SubmitFormProps['values'];

export interface CanHaveHubspotRegion {
  /**
   * The region associated with a given HubSpot account, where the form with a specified ID lives.
   *
   * @see formId
   *
   * @default DEFAULT_HUBSPOT_REGION
   */
  hubspotRegion?: HubspotRegion;
}

export interface HasHubspotFormId {
  /**
   * The unique ID of the form we'd like to get.
   */
  hubspotFormId: HubspotFormId;
}
